import React from 'react';
import {
  Document,
  DocumentAudio,
  DocumentBlank,
  DocumentPdf,
  DocumentVideo,
  Image,
  TableSplit,
} from '@carbon/icons-react';
import {
  AudioIcon,
  GenericFileIcon,
  PdfIcon,
  PhotoIcon,
  PptxIcon,
  PresentationIcon,
  RtfIcon,
  SpreadsheetIcon,
  VideoIcon,
  WordDocIcon,
  XlsxIcon,
} from '@ditioas/ui-graphics';
import {
  CsvFile,
  DocFile,
  JpgFile,
  Mp3File,
  Mp4File,
  PngFile,
  PptFile,
  PresentationFile,
  SvgFile,
  WmvFile,
  XlsFile,
} from '@ditioas/web-ui';

import cn from 'libs/cn';

export const getFileExtensionFromName = (fileName: string) =>
  fileName.split('.').pop();

type FileIconProps = {
  fileExtension: string;
  className?: string;
} & (
  | {
      new: true;
      size?: number;
      width?: never;
      height?: never;
    }
  | {
      new?: false;
      width?: string;
      height?: string;
      size?: never;
    }
);

const FileIcon = ({
  fileExtension,
  width,
  height,
  size,
  className,
  new: isNew,
}: FileIconProps) => {
  if (isNew) {
    switch (fileExtension) {
      case 'doc':
      case 'docx':
        return <DocFile size={size} className={cn('shrink-0', className)} />;
      case 'pdf':
        return (
          <DocumentPdf size={size} className={cn('shrink-0', className)} />
        );
      case 'png':
        return <PngFile size={size} className={cn('shrink-0', className)} />;
      case 'jpg':
        return <JpgFile size={size} className={cn('shrink-0', className)} />;
      case 'jpeg':
        return <Image size={size} className={cn('shrink-0', className)} />;
      case 'svg':
        return <SvgFile size={size} className={cn('shrink-0', className)} />;
      case 'rtf':
      case 'pages':
        return <Document size={size} className={cn('shrink-0', className)} />;
      case 'xls':
      case 'xlsx':
        return <XlsFile size={size} className={cn('shrink-0', className)} />;
      case 'csv':
        return <CsvFile size={size} className={cn('shrink-0', className)} />;
      case 'ppt':
      case 'pptx':
        return <PptFile size={size} className={cn('shrink-0', className)} />;
      case 'key':
        return (
          <PresentationFile size={size} className={cn('shrink-0', className)} />
        );
      case 'numbers':
        return <TableSplit size={size} className={cn('shrink-0', className)} />;
      case 'mp3':
        return <Mp3File size={size} className={cn('shrink-0', className)} />;
      case 'wmv':
        return <WmvFile size={size} className={cn('shrink-0', className)} />;
      case 'wav':
      case 'ogg':
      case 'flac':
        return (
          <DocumentAudio size={size} className={cn('shrink-0', className)} />
        );
      case 'mp4':
        return <Mp4File size={size} className={cn('shrink-0', className)} />;
      case 'avi':
      case 'mpeg':
      case 'mov':
      case 'mkv':
      case 'm4v':
        return (
          <DocumentVideo size={size} className={cn('shrink-0', className)} />
        );

      default:
        return (
          <DocumentBlank size={size} className={cn('shrink-0', className)} />
        );
    }
  } else {
    switch (fileExtension) {
      case 'doc':
      case 'docx':
        return (
          <WordDocIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'pdf':
        return (
          <PdfIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'svg':
        return (
          <PhotoIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'rtf':
      case 'pages':
        return (
          <RtfIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'xls':
      case 'xlsx':
      case 'csv':
        return (
          <XlsxIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'ppt':
      case 'pptx':
        return (
          <PptxIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'key':
        return (
          <PresentationIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'numbers':
        return (
          <SpreadsheetIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'flac':
        return (
          <AudioIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
      case 'avi':
      case 'mp4':
      case 'mpeg':
      case 'mov':
      case 'mkv':
      case 'm4v':
      case 'wmv':
        return (
          <VideoIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );

      default:
        return (
          <GenericFileIcon
            width={width}
            height={height}
            className={cn('shrink-0', className)}
          />
        );
    }
  }
};

export default FileIcon;
